import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import "@/App.css";
import { NAV_ITEMS } from "@/data";
import { getLenis, scrollToSection } from "@/lib/scroll";

const SPY_IDS = NAV_ITEMS.map((n) => n.id);

export default function Header() {
    const [active, setActive] = useState("");
    const [open, setOpen] = useState(false);

    useEffect(() => {
        let ticking = false;
        const update = () => {
            ticking = false;
            const y = window.scrollY + 160;
            let current = "";
            for (const id of SPY_IDS) {
                const el = document.getElementById(id);
                if (el && el.offsetTop <= y) current = id;
            }
            setActive(current);
        };
        const onScroll = () => {
            if (!ticking) {
                ticking = true;
                requestAnimationFrame(update);
            }
        };
        update();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const lenis = getLenis();
        if (open) {
            lenis?.stop();
            document.body.style.overflow = "hidden";
        } else {
            lenis?.start();
            document.body.style.overflow = "";
        }
    }, [open]);

    const go = (e, id) => {
        e.preventDefault();
        const wasOpen = open;
        setOpen(false);
        setTimeout(
            () => scrollToSection(id === "top" ? "#top" : `#${id}`),
            wasOpen ? 260 : 0,
        );
    };

    return (
        <>
            <header>
                <div className="header-inner">
                <a
                    href="#top"
                    className="logo"
                    data-testid="header-logo"
                    onClick={(e) => go(e, "top")}
                >
                    <img
                        src="/logo.svg"
                        alt="SoftwareFairGo scales seal"
                        className="logo-mark"
                        data-testid="header-logo-mark"
                    />
                    SoftwareFairGo
                </a>
                <nav className="desktop-nav" aria-label="Primary">
                    {NAV_ITEMS.map((item) => (
                        <a
                            key={item.id}
                            href={`#${item.id}`}
                            data-testid={`nav-link-${item.id}`}
                            className={`nav-link ${active === item.id ? "active" : ""}`}
                            onClick={(e) => go(e, item.id)}
                        >
                            {item.label}
                        </a>
                    ))}
                    <a
                        href="#contact"
                        className="nav-cta"
                        data-testid="nav-cta-book"
                        onClick={(e) => go(e, "contact")}
                    >
                        Reach us
                    </a>
                </nav>
                <button
                    className="menu-toggle"
                    data-testid="header-menu-toggle"
                    aria-label={open ? "Close menu" : "Open menu"}
                    aria-expanded={open}
                    onClick={() => setOpen(!open)}
                >
                    {open ? (
                        <X size={22} strokeWidth={1.5} />
                    ) : (
                        <Menu size={22} strokeWidth={1.5} />
                    )}
                </button>
            </div>
            </header>
            <AnimatePresence>
                {open && (
                    <motion.div
                        className="mobile-drawer"
                        data-testid="mobile-menu"
                        initial={{ opacity: 0, y: -14 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -14 }}
                        transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <nav aria-label="Mobile">
                            {[
                                ...NAV_ITEMS,
                                { id: "contact", label: "Reach us" },
                            ].map((item, i) => (
                                <motion.a
                                    key={item.id}
                                    href={`#${item.id}`}
                                    className={`mobile-link ${item.id === "contact" ? "mobile-cta" : ""}`}
                                    data-testid={`mobile-nav-link-${item.id}`}
                                    onClick={(e) => go(e, item.id)}
                                    initial={{ opacity: 0, y: 18 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        delay: 0.06 + i * 0.06,
                                        duration: 0.45,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                >
                                    <span className="mobile-link-num">
                                        0{i + 1}
                                    </span>
                                    {item.label}
                                </motion.a>
                            ))}
                        </nav>
                        <div className="mobile-drawer-foot">
                            hello@softwarefairgo.com.au
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
